import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivity } from '../hooks/useActivity';
import { useTeam } from '../context/TeamContext';
import type { ActivityItem } from '../utils/apiClient';
import { AssigneeAvatar } from './AssigneeAvatar';

function verbFor(item: ActivityItem): string {
  switch (item.type) {
    case 'note_added':
      return 'added a note on';
    case 'reassigned':
      return 'reassigned';
    case 'renamed':
      return 'renamed';
    case 'rescoped':
      return 'moved';
    case 'reprioritized':
      return 'reprioritized';
    case 'due_date_set':
      return 'set a due date on';
    case 'due_date_cleared':
      return 'cleared the due date on';
    case 'status_changed':
      return 'changed status of';
    case 'created':
      return 'created';
    default:
      return 'updated';
  }
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.max(1, Math.floor((now - then) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function badgeText(count: number): string {
  if (count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
}

export function ActivityBell() {
  const [open, setOpen] = useState(false);
  const { items, unreadCount, lastReadAt, markAllRead } = useActivity();
  const { byId } = useTeam();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleOpen() {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && unreadCount > 0) {
      void markAllRead();
    }
  }

  function navigateToTask(item: ActivityItem) {
    setOpen(false);
    navigate(`/group/${encodeURIComponent(item.teamId)}?t=${item.taskId}`);
  }

  const bt = badgeText(unreadCount);

  return (
    <div className="activity-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`activity-bell ${open ? 'is-open' : ''}`}
        onClick={handleOpen}
        aria-label={`Activity${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Activity"
      >
        <span className="activity-bell-icon" aria-hidden="true">🔔</span>
        {bt && <span className="activity-bell-badge">{bt}</span>}
      </button>
      {open && (
        <div className="activity-popover" role="dialog" aria-label="Activity">
          <header>
            <h4>Activity</h4>
            {unreadCount > 0 && (
              <button type="button" className="activity-mark-read" onClick={() => void markAllRead()}>
                Mark all read
              </button>
            )}
          </header>
          {items.length === 0 ? (
            <div className="activity-empty muted">No recent activity.</div>
          ) : (
            <ul>
              {items.map((item) => {
                const actor = byId(item.actorUserId);
                const isUnread = !lastReadAt || item.createdAt > lastReadAt;
                return (
                  <li key={item.eventId}>
                    <button
                      type="button"
                      className={`activity-item ${isUnread ? 'is-unread' : ''}`}
                      onClick={() => navigateToTask(item)}
                    >
                      <AssigneeAvatar member={actor} size={24} />
                      <div className="activity-text">
                        <div>
                          <strong>{actor?.displayName ?? 'Someone'}</strong>{' '}
                          <span className="muted">{verbFor(item)}</span>{' '}
                          <span className="activity-task-title">{item.taskTitle}</span>
                        </div>
                        <div className="muted activity-time">{timeAgo(item.createdAt)}</div>
                      </div>
                      {isUnread && <span className="activity-unread-dot" aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
